import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { isEqual } from 'lodash';

const CartContext = createContext();

// Helper function to safely parse session storage data
const getCartFromSession = () => {
  try {
    const cartData = sessionStorage.getItem('cart');
    return cartData ? JSON.parse(cartData) : [];
  } catch (error) {
    console.error('Error parsing cart data from sessionStorage', error);
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(getCartFromSession());
  const [menuData, setMenuData] = useState({
    food: null,
    drinks: null,
    specials: null,
  });

  // Generate unique cart ID for items with configurations
  const generateCartId = (item, config = {}) => {
    const baseId = item._id || item.id;
    return `${baseId}-${JSON.stringify(config)}`;
  };

  // Add item to cart
  const handleAddItem = (item, config = {}) => {
    const cartId = generateCartId(item, config);
    const baseId = item._id || item.id;

    // Determine item type
    let itemType = 'food';
    if (
      menuData.drinks?.categories?.some(cat =>
        cat.items?.some(
          menuItem => menuItem._id === baseId || menuItem.id === baseId
        )
      )
    ) {
      itemType = 'drinks';
    } else if (
      menuData.specials?.categories?.some(cat =>
        cat.items?.some(
          menuItem => menuItem._id === baseId || menuItem.id === baseId
        )
      )
    ) {
      itemType = 'specials';
    }

    // Calculate price including variations, addons, and customizations
    let price = config.variations?.price || item.price;
    if (config.addons?.length > 0) {
      price += config.addons.reduce((sum, addon) => sum + addon.price, 0);
    }
    if (config.customizations) {
      price += Object.values(config.customizations).reduce(
        (sum, option) => sum + (option.price || 0),
        0
      );
    }

    setCartItems(prevItems => {
      const existingIndex = prevItems.findIndex(i => i.cartId === cartId);

      if (existingIndex > -1) {
        return prevItems.map((item, index) =>
          index === existingIndex
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }

      return [
        ...prevItems,
        {
          ...item,
          cartId,
          baseId,
          price,
          quantity: 1,
          itemType,
          configuration: config,
        },
      ];
    });
  };

  // Decrease item quantity
  const handleDecreaseItem = cartId => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.cartId === cartId);
      if (!existingItem) return prevItems;

      if (existingItem.quantity > 1) {
        return prevItems.map(item =>
          item.cartId === cartId
            ? { ...item, quantity: item.quantity - 1 }
            : item
        );
      }

      return prevItems.filter(item => item.cartId !== cartId);
    });
  };

  // Update menu data only if it changed
  const updateMenuData = newData => {
    setMenuData(prev => {
      // Use deep comparison to prevent unnecessary updates
      return isEqual(prev, newData) ? prev : newData;
    });
  };

  // Clear cart completely
  const clearCart = () => {
    setCartItems([]);
    sessionStorage.removeItem('cart');
  };

  // Persist cart to session storage when it changes
  useEffect(() => {
    const venueId = localStorage.getItem('venueId');
    const storedCartId = sessionStorage.getItem('cartId');

    // Clear cart if venue changed
    if (storedCartId && storedCartId !== venueId && cartItems.length > 0) {
      clearCart();
      return;
    }

    // Only save to session if we have items
    if (cartItems.length > 0) {
      sessionStorage.setItem('cartId', venueId);
      sessionStorage.setItem('cart', JSON.stringify(cartItems));
    }
  }, [cartItems]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      cartItems,
      menuData,
      handleAddItem,
      handleDecreaseItem,
      updateMenuData,
      clearCart,
    }),
    [cartItems, menuData]
  );

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
